import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  managementQuery,
  post,
  type BoardPanel,
  type BoardPanelPoint,
  type BoardSilence,
  type BoardDistribution,
  type BoardExplained,
  type BoardSlice,
  type BoardTopApps,
  type BoardUntaggedApps,
  type UntaggedApp,
} from '../api/productivitee'
import { shellQuery } from '../api/shell'
import type { Period } from '../api/securitee'
import { durationString } from '../easeedesk/format'
import { rangeOf } from '../easeedesk/dashboards/range'
import { BoardGrid, BoardLook, BoardStat, BoardStrip } from '../dashboard/BoardLook'
import { OdbCard } from '../dashboard/OdbCard'
import { ChartEmpty } from '../inventoree/EndpointCharts'
import { PeriodFilter } from '../securitee/DevicesPage'
import { PERIOD_PRESETS } from './period'
import { Delta, METRIC_LABEL, SHARE_HINT, TrendChart, TrendLegend, type Metric } from './EvolutionChart'
import { daysBefore } from './UserEvolution'

/**
 * TABLOUL DE CONDUCERE al lui Productivitee - `productivitee/management`.
 *
 * -----------------------------------------------------------------------------
 * CE ARATA, SI CE NU ARATA
 * -----------------------------------------------------------------------------
 *
 * Firma pe o perioada: cum a mers in timp, si cum stau sediile si
 * departamentele unul fata de altul, cu perioada dinainte alaturi.
 *
 * NICIUN NUME DE OM. Nu e o scapare si nu e o fila care lipseste: urmarirea
 * unei persoane cu nume are legile ei, iar un clasament de oameni pus sub ochii
 * conducerii e chiar lucrul pentru care se cer temeiuri scrise. Omul cu numele
 * lui a ramas unde era - `user_productivity` si `user_productivity_period` -
 * sub dreptul care il pazea si pana acum. Aici se aduna: sediul,
 * departamentul, firma. Capatul din spate nici nu trimite vreun nume.
 *
 * -----------------------------------------------------------------------------
 * DE CE E PE DESENUL TABLOURILOR, SI NU PE RANDURI DE BOOTSTRAP
 * -----------------------------------------------------------------------------
 *
 * Roxana, 21 septembrie 2026: "nu arata ca design ca dashboard, fa-l si tu pe
 * dashboardul nou".
 *
 * Asa era: patru dale albe scrise de mana, doua `alert` de bootstrap si doua
 * tabele `NestedTable` lipite de cate un `h4.bordered`. Ecranul spunea lucruri
 * de tablou intr-o limba pe care portalul o parasise - si le spunea la o zi
 * dupa ce tabloul Productivitee de alaturi trecuse pe desenul nou (livrarile
 * 26.9.21.7 si .11).
 *
 * Acum e acolo unde ii e locul: `.odb-page` cu foaia `od_dashboard.css`, o
 * BANDA de contoare sus (`BoardStrip` / `BoardStat`) si o GRILA de carduri
 * dedesubt (`BoardGrid` / `OdbCard`) - aceleasi cutii, aceeasi tipografie,
 * aceeasi placuta de modul, acelasi "No data yet" ca pe tabloul de alaturi si
 * ca pe celelalte tablouri de MASURATORI (Endpoint Performance, SNMP, Core
 * Applications). Nu se scrie nicio cutie noua si niciun hexa: tot ce se vede
 * vine din piesele alea.
 *
 * DE CE NU `ModuleBoardPage`, si de ce nu `BoardWindow` din capul lui: tabloul
 * de modul isi ia cardurile, perspectivele si fereastra de timp din
 * `application/Services/Dashboard/Catalog.php`, fisier COMUN cu portalul care
 * ruleaza la banca, in care Productivitee n-are niciun rand. Si, mai ales,
 * `BoardWindow` lucreaza cu ADRESE (`hrefFor`) - tabloul de modul isi tine
 * fereastra in `?days=`/`?from=`. Ecranul asta isi tine perioada in stare, iar
 * adresa lui nu poarta nimic; mutat pe `BoardWindow` ar fi insemnat ori o
 * adresa noua, ori legaturi mincinoase. Deci se ia DESENUL benzii de pastile -
 * `button.odb-window`, randurile scrise in foaie anume pentru tablourile unde
 * "alegerea se face pe loc, fara sa se schimbe adresa" - si se lasa masinaria.
 * Ce cere ecranul de la server nu s-a schimbat cu nimic: acelasi
 * `managementQuery(from, to, office)`.
 *
 * -----------------------------------------------------------------------------
 * DE UNDE VINE FIECARE CIFRA - SI DE CE SCRIE ASTA PE ECRAN
 * -----------------------------------------------------------------------------
 *
 * Cele doua parti ale modulului nu au NICIUN tabel comun (vezi proba celor
 * sapte porti, livrarea 26.9.21.8):
 *
 *   - partea OAMENILOR (`it_logged_users_list` + `it_productivitee_user_summary`)
 *     da timpul de lucru, cel activ si SEDIUL. Un rand pe om pe zi, deci cine
 *     are doua masini e numarat o data;
 *   - partea MASINILOR (`it_productivitee_device_summary` + `it_echipamente`)
 *     da DEPARTAMENTUL si cifra de acoperire a masinilor.
 *
 * Deci departamentul unui om e imprumutat de la masinile pe care a lucrat, si
 * i se pune cel pe care a stat cele mai multe zile. Scrie pe ecran, sub tabel:
 * o cifra a carei provenienta nu se vede se citeste gresit chiar si de cine o
 * face.
 *
 * Iar zilele perioadei nu vin toate din acelasi tabel: cele mutate pe rollup-ul
 * pe (om, zi) vin din REUNIUNEA intervalelor - aceeasi pe care o arata lista
 * User Productivity - si cele dinaintea lui din sumarul vechi, care ADUNA
 * duratele. Cand perioada le amesteca, scrie pe ecran cate si intre ce date.
 *
 * -----------------------------------------------------------------------------
 * DOUA CARDURI DE APLICATII, DOUA INTREBARI
 * -----------------------------------------------------------------------------
 *
 * "Where the day goes" si "Untagged applications" citesc amandoua aceeasi
 * fereastra de sapte zile din istoric si pot arata, pe o firma proaspata, chiar
 * aceleasi randuri. Nu sunt acelasi card:
 *
 *   - primul nu taie nimic si n-are niciun buton - spune in ce programe se duce
 *     ziua, cu partea fiecaruia din timpul ferestrei si cu coada socotita;
 *   - al doilea e taiat pe ce n-are eticheta si are butonul de etichetat pe
 *     rand - o lista de treaba de facut, care se scurteaza pe masura ce se
 *     lucreaza in ea.
 *
 * Scrie pe amandoua, in nota, fiindca doua liste de programe una sub alta se
 * citesc altfel decat au fost puse.
 *
 * -----------------------------------------------------------------------------
 * UN SINGUR CLASAMENT, SI UN CARD CARE NU E CLASAMENT
 * -----------------------------------------------------------------------------
 *
 * Erau DOUA clasamente - Offices si Departments - si amandoua raspundeau la
 * aceeasi intrebare: cine e primul. Raspunsul nu se schimba de la o zi la alta,
 * fiindca il hotarasc masinile uitate aprinse, si nu se mai uita nimeni la el.
 *
 * Deci a plecat cel de DEPARTAMENTE, si in locul lui stau panouri mici, cate
 * unul pe departament, fiecare fata de EL INSUSI: intrebarea nu mai e "cine e
 * primul", e "cine s-a SCHIMBAT". Cel de SEDII a ramas - sediul e alta taietura,
 * si cardul de panouri nu-l acopera. Doua carduri care raspund la aceeasi
 * intrebare nu inseamna doua raspunsuri, inseamna unul citit de doua ori.
 *
 * -----------------------------------------------------------------------------
 * CIFRELE DE ONESTITATE
 * -----------------------------------------------------------------------------
 *
 * Sus, in banda: cati oameni si cate masini au raportat din cati erau
 * asteptati. Un procent de productivitate socotit peste o masuratoare gaurita e
 * o minciuna spusa cu incredere; tabloul isi spune gaurile singur, si le
 * APRINDE cand trec de prag - cu tonul cardurilor (`odb-t-crit`), nu cu un rosu
 * scris de mana.
 *
 * A treia cifra de onestitate - cat din timpul masurat a stat in aplicatii pe
 * care nimeni nu le-a etichetat - A PLECAT de pe ecran pe 24 septembrie 2026,
 * cu tot cu cardul ei "Measured time", fiindca se citea din
 * `it_productivitee_history.tag` si randul fara eticheta se scrie acolo cu
 * `tag = 1`: felia ar fi fost de acum incolo mereu zero. In locul cardului plecat
 * sta "Where the day goes".
 *
 * CIFRA S-A INTORS pe 25 septembrie 2026, ca RAND de `.odb-lead`, socotita din
 * partea care chiar stie: tabelul de ETICHETARE, peste toate aplicatiile
 * ferestrei (`top_apps.untagged_share`). Nu e o infrumusetare, e capcana
 * ecranului - pe instanta asta programul neetichetat se socoteste PRODUCTIV, deci
 * procentul din banda e in parte o presupunere, si pana atunci nimic pe ecran nu
 * spunea cat de mare e partea aia. Randul nu se aprinde la niciun prag: nu exista
 * un numar apararabil pentru "prea mult neetichetat".
 *
 * -----------------------------------------------------------------------------
 * NICIO SUMA PESTE O PERIOADA SAU PESTE OAMENI
 * -----------------------------------------------------------------------------
 *
 * Roxana, 25 septembrie 2026: "aici sunt multe sume care nu se inteleg... si poate
 * il faci mai relevant". Livrarea 26.9.25.3 pusese medii pe banda si pe clasament;
 * cardurile de aplicatii si golurile explicate raamsesera pe sume, cu motivul ca
 * procentele randurilor sunt "partea din total" si o medie ar rupe citirea. Motivul
 * era bun, raspunsul era "amandoua": PROCENTUL RAMANE - el e partea din zi - si
 * langa el, in locul sumei, sta media PE ZI-OM. Asa cifra inseamna acelasi lucru pe
 * o firma cu trei oameni si pe una cu trei sute.
 *
 * Ce NU s-a atins: mediana din "Active time per person-day" (o mediana nu se
 * inlocuieste cu o medie) si coloanele graficului, care sunt sume in treapta lor,
 * nu peste perioada.
 *
 * PATRU contoare in banda, nu cinci: `.odb-strip` pune cate incap la minimum
 * 240px, si la 1280 al cincilea cadea singur pe randul doi, cu trei sferturi de
 * rand gol langa el (masurat in Edge). Patru stau pe un rand si la 1280, si la
 * 1600.
 *
 * Propozitiile care spun CE INSEAMNA o acoperire proasta stau imediat sub
 * banda, in `.odb-lead` - randul pe care tablourile il folosesc pentru ce
 * trebuie citit INAINTE de cifrele de dedesubt. Sunt deasupra grilei, deci
 * deasupra clasamentelor, si nu mai stau lipite de capul primului tabel. Cand
 * amandoua acoperirile sunt bune, randul nu se mai deseneaza deloc.
 *
 * Randul despre timpul neetichetat nu mai exista deloc: cifra pe care o spunea
 * se citea din `it_productivitee_history.tag`, si de pe 24 septembrie 2026
 * coloana aia nu mai stie ce e neetichetat.
 *
 * -----------------------------------------------------------------------------
 * CE NU SE FOLOSESTE AICI, CU BUNA STIINTA
 * -----------------------------------------------------------------------------
 *
 * "Average Productivity" de pe tabloul modulului scrie 0.00% si in portalul
 * vechi, si in v4: functia din baza e chemata cu o data acolo unde asteapta un
 * numar. E o hotarare separata a Roxanei, si ecranul asta NU se sprijina pe ea
 * - cifra desenata e TIMPUL ACTIV din timpul de lucru, amandoua scrise de
 * agent in acelasi rand.
 */

const METRICS: Metric[] = ['share', 'active_time', 'working_time']

/** Sub pragul asta de acoperire, cifra se aprinde: masuratoarea e prea gaurita ca sa poarte o concluzie. */
const POOR_COVERAGE = 70

/** Ultimele 30 de zile, ferestra cu care se deschide ecranul. */
function lastThirtyDays(): Period {
  const now = new Date()
  const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return { from: daysBefore(to, 29), to }
}

/**
 * COLOANELE unui clasament, scrise o data pentru capul listei si pentru randuri.
 *
 * Randul de lista al tablourilor (`.odb-row`) are un nume la stanga si o cifra
 * la dreapta, si atat - potrivit pentru un clasament de cinci randuri cu un
 * singur raspuns pe rand. Aici fiecare sediu poarta CINCI cifre, si ele nu se
 * pot ascunde intr-un sertar: pe tabelul de dinainte se vedeau toate deodata,
 * iar o mutare de haine n-are voie sa ia nimic de pe ecran.
 *
 * Deci cifrele stau in celule de latime FIXA, aliniate la dreapta, cu un singur
 * rand de etichete deasupra listei. Latimile se scriu aici, o data, fiindca
 * altfel capul si randurile se despart la prima schimbare si coloanele ies
 * strambe - exact felul de lucru pe care nimeni nu-l mai indreapta dupa.
 *
 * Numele celor trei masuri se iau din `METRIC_LABEL`, nu se scriu inca o data:
 * ecranul le foloseste si pe pastilele de deasupra graficului, si aceeasi
 * masura trebuie sa se cheme la fel in amandoua locurile.
 *
 * LATIMILE, dupa ce Roxana a spus (21 septembrie 2026) ca eticheta capului
 * "scrie de sus in jos nu se citeste": prima data erau croite pe cifra (62-84px),
 * dar eticheta e mai lata decat cifra ei - "Working time" cere 96px, iar in 74
 * se rupea cuvant sub cuvant. Acum fiecare coloana e cat ii trebuie ETICHETEI,
 * masurat in Edge cu Open Sans, foaia adevarata:
 *
 *   - "People", "Working time", "Active time" incap pe UN rand (Roxana le voia
 *     stanga-dreapta);
 *   - cele doua coloane de procent - "Active time (%)" si "... vs previous" -
 *     poarta de doua ori acelasi nume lung; ele cad curat pe DOUA randuri
 *     (nume/unitate, nume/"vs previous"), aliniate jos, nu cuvant cu cuvant.
 *
 * `people` e larga cat "150 (was 140)" (77px), nu cat "People", ca un sediu cu
 * suta de oameni sa nu impinga cifra din coloana. Sub ~1200px, unde cardul de
 * doua celule e la jumatate de ecran, numele sediului se scurteaza cu trei
 * puncte (`.odb-row-label` are `text-overflow: ellipsis`), NU se rupe.
 */
/*
 * Cele doua coloane de TIMP sunt MEDII PE ZI-OM, nu sume pe perioada - Roxana,
 * 25 septembrie 2026: "si in dashboarduri tot average nu mai face sume ca nu e
 * relevant". "1247h" intr-un sediu nu spunea nimic despre el: crestea cu
 * fiecare angajat si cu fiecare zi de perioada, deci clasa sediile dupa cat
 * sunt de mari, nu dupa cum lucreaza. Numitorul (`person_days`) e scris in nota
 * cardului.
 */
const SLICE_COLS: { key: string; label: string; width: number }[] = [
  { key: 'people', label: 'People', width: 80 },
  { key: 'working_time', label: `${METRIC_LABEL.working_time} / person-day`, width: 100 },
  { key: 'active_time', label: `${METRIC_LABEL.active_time} / person-day`, width: 82 },
  { key: 'share', label: METRIC_LABEL.share, width: 82 },
  { key: 'delta', label: `${METRIC_LABEL.share} vs previous`, width: 106 },
]

/** Spatiul dintre celulele unui rand de clasament, in capul listei si in randuri. */
const SLICE_GAP = 6

/**
 * Cardul fara ce desena spune asta LINISTIT, cu vorbele cardurilor din catalog:
 * "No data yet", si dedesubt de ce - la fel ca pe tabloul Productivitee de
 * alaturi, niciodata un desen gol sau un tabel cu zero randuri.
 */
function NoData({ children }: { children: ReactNode }) {
  return (
    <ChartEmpty>
      <span>
        <span className="odb-unknown-why">No data yet</span>
        <br />
        {children}
      </span>
    </ChartEmpty>
  )
}

/**
 * FEREASTRA CELOR DOUA CARDURI DE APLICATII, pe FATA cardului si mereu.
 *
 * -----------------------------------------------------------------------------
 * DE CE SUS, SI NU IN NOTA DE JOS
 * -----------------------------------------------------------------------------
 *
 * Roxana, 25 septembrie 2026: "aici sunt multe sume care nu se inteleg". Jumatate
 * din nedumerire venea de aici: omul alege o LUNA sus pe ecran, si doua carduri
 * ii raspund despre SAPTE ZILE. Scria - dar a treia propozitie a unei note de
 * cinci, sub randuri, in cel mai mic corp de litera din card. Cine citeste cifra
 * din cap si randurile de la mijloc nu ajunge niciodata acolo.
 *
 * Acum e primul lucru dinaintea randurilor, si poarta si NUMITORUL mediilor de
 * pe ele: o medie fara numitor minte, si numitorul asta nu e nici cel al benzii
 * de sus (perioada intreaga), nici numarul de zile.
 *
 * -----------------------------------------------------------------------------
 * "NOT THE WHOLE PERIOD" NUMAI CAND CHIAR NU E
 * -----------------------------------------------------------------------------
 *
 * Pe "Last 7 days" - una din cele patru pastile - fereastra E perioada, si pana
 * pe 25 septembrie 2026 cardul scria oricum "the last 7 days of the period, not
 * the whole period". Era fals, si era fals exact pe perioada pe care cardul
 * raspunde cel mai bine. Serverul spune acum daca a tuns ceva (`trimmed`), si
 * randul se scrie dupa el.
 */
function AppWindow({ apps }: { apps: { from: string; to: string; days: number; trimmed: boolean; person_days: number } }) {
  return (
    <p className="odb-note" style={{ marginTop: 0 }}>
      {apps.trimmed ? `The last ${apps.days} days of the period` : 'This period'} ({apps.from} - {apps.to}), over{' '}
      {apps.person_days} person-day{apps.person_days === 1 ? '' : 's'}.
    </p>
  )
}

/** Capul listei: numele celor cinci cifre, peste celulele lor. */
function SliceHead() {
  return (
    <div
      className="odb-note"
      style={{ display: 'flex', alignItems: 'flex-end', gap: SLICE_GAP, margin: '0 10px 6px', textTransform: 'uppercase', letterSpacing: '.06em' }}
    >
      <span style={{ flex: '1 1 auto', minWidth: 0 }} />
      {SLICE_COLS.map((c) => (
        <span key={c.key} style={{ flex: `0 0 ${c.width}px`, textAlign: 'right' }}>
          {c.label}
        </span>
      ))}
    </div>
  )
}

/**
 * Un sediu sau un departament, pe randul lui de lista.
 *
 * Bara din spate (`.odb-row-fill`) spune cat inseamna randul fata de PRIMUL din
 * clasament, in culoarea modulului - forma pe care o au toate listele
 * tablourilor. Nu e o culoare de stare: un sediu al doilea nu e "rau", iar
 * starea o poarta cifra de sus.
 */
function SliceRow({ row, top }: { row: BoardSlice; top: number }) {
  const cell = (key: string): React.CSSProperties => {
    const col = SLICE_COLS.find((c) => c.key === key)
    /* `nowrap`: cifra randului nu se rupe niciodata pe verticala - nici
       "150 (was 140)" dintr-un sediu mare in coloana lui de latime fixa. Capul
       nu-l primeste: acolo cele doua coloane de procent CHIAR trebuie sa cada pe
       doua randuri. */
    return { flex: `0 0 ${col?.width ?? 70}px`, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
  }

  return (
    <div className="odb-row">
      <span className="odb-row-fill" style={{ width: `${top > 0 ? Math.round((row.share * 100) / top) : 0}%` }} />
      <span className="odb-row-label">
        <span className="odb-of" style={{ marginRight: 6 }}>{row.rank}</span>
        {row.name}
      </span>
      {/* Cifrele randului scriu mai marunt decat cifra obisnuita a unei liste
          (16px, ingrosata): cinci intr-un singur rand la marimea din foaie ar fi
          rupt randul in doua si bara din spate ar fi lasat a doua linie
          descoperita - masurat la 1280, unde cardul are 598px. Procentul, care e
          raspunsul clasamentului, ramane apasat. */}
      <span className="odb-row-value" style={{ display: 'flex', alignItems: 'baseline', gap: SLICE_GAP, fontSize: 12, fontWeight: 400, flex: '0 0 auto' }}>
        <span style={cell('people')}>
          {row.people} <span className="color-light-grey">(was {row.prev_people})</span>
        </span>
        {/* Media pe zi-om, socotita pe server. `null` (niciun cap raportat) se
            scrie cu o liniuta: un "0s" ar fi aratat ca o masuratoare. */}
        <span style={cell('working_time')}>{row.avg_working_time === null ? '-' : durationString(row.avg_working_time)}</span>
        <span style={cell('active_time')}>{row.avg_active_time === null ? '-' : durationString(row.avg_active_time)}</span>
        <span style={{ ...cell('share'), fontWeight: 600 }}>{row.share}%</span>
        <span style={cell('delta')}>
          <Delta value={row.delta} />
        </span>
      </span>
    </div>
  )
}

/**
 * Un clasament, in cardul lui.
 *
 * Lista se deruleaza IN card (`.odb-list`): celula grilei are inaltime fixa, si
 * o firma cu douazeci de sedii ar fi avut randurile de sub margine taiate fara
 * ca nimic de pe ecran sa spuna ca mai e ceva acolo. Nota de sub lista - de unde
 * vine cifra - ramane lipita de fundul cardului, unde se citeste dupa cifre, nu
 * inaintea lor.
 */
function Ranking({
  id,
  title,
  icon,
  rows,
  note,
  empty,
}: {
  id: string
  title: string
  icon: string
  rows: BoardSlice[]
  note: string
  empty: string
}) {
  const top = rows.length > 0 ? Math.max(...rows.map((r) => r.share)) : 0

  return (
    <OdbCard module="productivitee" icon={icon} title={title} value={rows.length} w={2} h={2} id={id}>
      {(height) => (
        <div style={{ height, display: 'flex', flexDirection: 'column' }}>
          {rows.length === 0 ? (
            <NoData>{empty}</NoData>
          ) : (
            <>
              <SliceHead />
              <div className="odb-list" style={{ flex: '1 1 auto', minHeight: 0 }}>
                {rows.map((row) => (
                  <SliceRow key={`${row.id}-${row.rank}`} row={row} top={top} />
                ))}
              </div>
            </>
          )}
          <p className="odb-note" style={{ marginBottom: 0 }}>{note}</p>
        </div>
      )}
    </OdbCard>
  )
}

/**
 * UNDE SE DUCE ZIUA ECHIPEI - primele aplicatii ale firmei, cu partea lor din zi.
 *
 * -----------------------------------------------------------------------------
 * CE STATEA AICI, SI DE CE A PLECAT
 * -----------------------------------------------------------------------------
 *
 * Statea "Measured time": trei durate - productiv, neproductiv, neetichetat -
 * adunate peste TOTI oamenii firmei pe toata perioada. Roxana, 24 septembrie
 * 2026: "ajuta `2days 1h 50min 18s` la ceva? mai bine pui alta informatie utila,
 * decat sume".
 *
 * Are dreptate de doua ori. "Doua zile si 1h50" nu e o marime: n-are numitor, se
 * misca si cand se schimba doar numarul de oameni care au raportat, si nu se
 * poate urmari pana la o cauza. Iar de pe 24 septembrie 2026 randul fara eticheta
 * se SCRIE in istoric cu `tag = 1` (se socoteste productiv - hotararea ei,
 * explicita), deci felia "Untagged" ar fi fost de acum incolo mereu zero si cea
 * "Productive" umflata cu tot timpul neetichetat. Pe o firma cu tot catalogul
 * neetichetat ar fi scris "0% neetichetat" la doua carduri de cel care numara,
 * din CATALOG, cate aplicatii n-au eticheta - doua cifre care s-ar fi contrazis
 * pe acelasi ecran.
 *
 * -----------------------------------------------------------------------------
 * DE CE ASTA IN LOC
 * -----------------------------------------------------------------------------
 *
 * Raspunde la o intrebare pe care un sef si-o pune chiar - in ce programe se duce
 * ziua echipei - si la care ecranul nu putea raspunde deloc. Nu atarna de nicio
 * eticheta: aduna secunde pe aplicatie, si secundele nu depind de ce crede cineva
 * despre program. Si are NUMITOR pe fiecare rand: partea din timpul ferestrei, nu
 * o durata singura.
 *
 * -----------------------------------------------------------------------------
 * NU E CARDUL DE NEETICHETATE SPUS A DOUA OARA
 * -----------------------------------------------------------------------------
 *
 * Pe o firma cu tot catalogul neetichetat cele doua pot arata chiar aceleasi
 * randuri, deci deosebirea trebuie sa se VADA, nu sa se subinteleaga:
 *
 *   - "Untagged applications" e taiat pe ce n-are eticheta si are butonul de
 *     etichetat pe rand: o lista de TREABA DE FACUT, care se scurteaza pe masura
 *     ce se lucreaza in ea;
 *   - asta nu taie nimic si n-are niciun buton: un RASPUNS, cu partea fiecarui
 *     program din timpul ferestrei si cu coada socotita. Cate din randurile
 *     desenate n-au inca eticheta scrie in nota, si nota trimite acolo.
 *
 * -----------------------------------------------------------------------------
 * CE SE VEDE CAND NU SUNT DATE
 * -----------------------------------------------------------------------------
 *
 * O firma proaspata, sau o fereastra dinaintea agentului, n-are niciun rand de
 * istoric: cardul o SPUNE si nu deseneaza nimic. Niciun zero convingator, si
 * niciun rand de zero secunde - o aplicatie a carei singura felie cadea in
 * intregime in alta nici nu ajunge pana aici (`HAVING` pe server).
 *
 * Desenul e `.odb-row` + `.odb-row-fill`, care sunt deja pe ecran. Niciun desen
 * nou. NICIUN NUME DE OM: numele de pe randuri sunt ale programelor
 * (`chrome.exe`, `facebook.com`), iar titlurile de fereastra nu se mai strang
 * deloc de pe 23 septembrie 2026.
 */
function TopApps({ apps }: { apps: BoardTopApps }) {
  const shown = apps.rows.reduce((sum, row) => sum + row.app_time, 0)
  /* Coada: ce au mancat aplicatiile care n-au incaput. Se socoteste din cifrele
     ferestrei INTREGI, pe care serverul le trimite dinaintea taierii. */
  const restTime = Math.max(0, apps.total - shown)
  const restApps = Math.max(0, apps.apps - apps.rows.length)
  /* Coada e o SCADERE facuta aici, deci si media ei: aceeasi rotunjire ca
     `Evolution::perDay`, ca randul de coada sa se citeasca la fel ca vecinii. */
  const restAvg = apps.person_days > 0 ? Math.round(restTime / apps.person_days) : null
  const top = apps.rows.length > 0 ? Math.max(...apps.rows.map((r) => r.app_time)) : 0
  const untagged = apps.rows.filter((r) => !r.tagged).length
  const share = (seconds: number) => Math.round((seconds * 1000) / Math.max(1, apps.total)) / 10
  /*
   * CIFRA DIN CAPUL CARDULUI: cat din fereastra tin randurile desenate.
   *
   * Pana pe 25 septembrie 2026 aici scria `apps.apps` - CATE programe deosebite a
   * vazut fereastra. "338", fara unitate si fara eticheta, si Roxana a intrebat
   * chiar asta: "ce inseamna 338". Nu insemna nimic pentru nicio hotarare - un sef
   * care se uita la "unde se duce ziua" intreaba IN CE se duce, nu cate programe
   * exista pe lume.
   *
   * Cifra de acum raspunde la o intrebare care se pune: cat din zi lamuresc
   * randurile de dedesubt. 90% inseamna ca ziua echipei sta in cateva unelte si
   * cardul spune care; 12% inseamna ca se imprastie in sute de programe si ca de
   * aici nu se poate porni nimic - iar asta e tot ce spunea "338", numai ca spus
   * ca o parte din intreg. Cifra nu se ia din numarul de randuri desenate al
   * nostru: randurile si coada se aduna la 100%, deci se citeste pe ecran.
   *
   * Numarul de programe n-a plecat de pe ecran: e pe randul de coada ("330 more
   * applications") si in nota.
   */
  const covered = apps.total > 0 ? Math.round((shown * 100) / apps.total) : 0

  return (
    <OdbCard
      module="productivitee"
      icon="fa-chart-pie"
      title="Where the day goes"
      value={apps.rows.length === 0 ? '-' : `${covered}%`}
      w={2}
      h={2}
      id="productivitee_board_top_apps"
    >
      {(height) => (
        <div style={{ height, display: 'flex', flexDirection: 'column' }}>
          <AppWindow apps={apps} />
          {apps.rows.length === 0 ? (
            <NoData>No machine reported an application in that window, so there is nothing to split.</NoData>
          ) : (
            <div className="odb-list" style={{ flex: '1 1 auto', minHeight: 0 }}>
              {apps.rows.map((app) => (
                <div className="odb-row" key={app.application_id}>
                  <span className="odb-row-fill" style={{ width: `${top > 0 ? Math.round((app.app_time * 100) / top) : 0}%` }} />
                  <span
                    className="odb-row-label"
                    title={`${app.application_name || 'no name'} (${app.application_type})${app.tagged ? '' : ' - no tag on any group yet'}`}
                  >
                    {app.application_name || <span className="odb-unknown-why">no name</span>}
                  </span>
                  <span className="odb-of">{share(app.app_time)}%</span>
                  {/* `nowrap`: o durata lunga - "2days 1h 50min 18s", pe care o
                      scoate o masina uitata aprinsa - se rupea pe doua randuri si
                      randul crestea de la 36 la 54px, lasand bara din spate sa
                      acopere numai prima linie (masurat in Edge, la 1280, unde
                      cardul are 598px). Numele de langa ea are deja `ellipsis`,
                      deci se scurteaza el.

                      TIMPUL E MEDIA PE ZI-OM, nu suma ferestrei peste toata firma
                      (Roxana, 25 septembrie 2026). Suma nu se mai deseneaza si nu
                      s-a pierdut nimic cu ea: procentul de alaturi spune deja cat
                      inseamna randul din fereastra, iar suma insasi e in nota.
                      Procentul RAMANE - el e partea din zi, si de aia e aici. */}
                  <span className="odb-row-value" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {app.avg_time === null ? '-' : durationString(app.avg_time)}
                  </span>
                </div>
              ))}
              {/* Coada, pe un rand al ei si in griul necunoscutului: fara ea cele
                  cateva randuri de sus ar fi parut toata ziua echipei. */}
              {restApps > 0 && (
                <div className="odb-row">
                  <span
                    className="odb-row-fill odb-c-flat"
                    style={{ width: `${top > 0 ? Math.round((Math.min(restTime, top) * 100) / top) : 0}%` }}
                  />
                  {/* Numai CULOAREA, cu semnul din foaie: `.odb-unknown-why`, clasa
                      care spune "necunoscut", poarta si o inaltime de rand a ei si
                      facea randul de coada cu 4px mai inalt decat vecinii lui
                      (masurat in Edge). */}
                  <span className="odb-row-label" style={{ color: 'var(--od-ink-faint)' }}>
                    {restApps} more application{restApps === 1 ? '' : 's'}
                  </span>
                  <span className="odb-of">{share(restTime)}%</span>
                  <span className="odb-row-value" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {restAvg === null ? '-' : durationString(restAvg)}
                  </span>
                </div>
              )}
            </div>
          )}
          <p className="odb-note" style={{ marginBottom: 0 }}>
            The figure on the card is how much of the window the rows below account for; the rest is the tail row. One row per
            application, ranked by time: the union of its intervals per machine per day, over every machine of the company,
            divided by the person-days that reported - a sum grew with every extra person and every extra day of the window, so
            it could not be read on a company of three or on one of three hundred. The percentage is its share of the whole
            window, {durationString(apps.total)} in {apps.apps} application{apps.apps === 1 ? '' : 's'}
            {apps.avg_total !== null && `, ${durationString(apps.avg_total)} per person-day`}. That total is the sum of the
            per-application unions, so a second in which a machine reported two programs at once is in both, and the total can be
            longer than anybody worked - which is also why the per-application averages can add up past the length of a day.
            {untagged > 0 &&
              ` ${untagged} of the applications drawn here have no tag yet; tagging them is the Untagged applications card, this one only says where the day goes.`}{' '}
            Nothing here is computed from a tag. Kept for up to {Math.round(apps.cached_seconds / 60)} minutes instead of being
            asked again on every load.
          </p>
        </div>
      )}
    </OdbCard>
  )
}

/**
 * CAT DIN GOLURI SI-A EXPLICAT ECHIPA.
 *
 * -----------------------------------------------------------------------------
 * CARDUL LIPSESTE CU TOTUL CAND RAPORTUL DE SEARA E STINS
 * -----------------------------------------------------------------------------
 *
 * `day_report_enabled` porneste STINS pentru fiecare firma. Cat timp e stins,
 * nimeni n-a fost intrebat nimic, si un "0% explicat" desenat cu bara si procent
 * ar fi cea mai proasta cifra de pe ecran: ar arata ca nimeni n-are nimic de
 * spus, cand de fapt nimeni n-a fost intrebat. Deci nu se deseneaza cardul - se
 * scrie un rand care spune unde se aprinde raportul.
 *
 * -----------------------------------------------------------------------------
 * REUNIUNE, SI DE CE SUMA FELIILOR POATE TRECE DE TOTAL
 * -----------------------------------------------------------------------------
 *
 * Randurile le scrie omul si se pot suprapune. Timpul explicat e REUNIUNEA lor pe
 * (om, zi); feliile pe fel se reunesc fiecare in ea, deci adunate pot trece de
 * total. Scrie pe card, in loc sa fie ascuns prin impartirea unui total.
 *
 * Desenul e `.odb-segments` + `.odb-row`, ca la celelalte defalcari ale ecranului.
 */
function ExplainedGaps({ explained }: { explained: BoardExplained }) {
  const top = Math.max(1, ...explained.by_kind.map((k) => k.seconds))
  const unexplained = Math.max(0, explained.inactive - explained.explained)

  return (
    <OdbCard
      module="productivitee"
      icon="fa-comment-dots"
      title="Explained gaps"
      value={`${explained.share}%`}
      w={2}
      h={2}
      id="productivitee_board_explained"
    >
      {(height) => (
        <div style={{ height, display: 'flex', flexDirection: 'column' }}>
          {explained.inactive === 0 ? (
            <NoData>No inactive time in the days this period has in the daily rollup, so there is nothing to explain.</NoData>
          ) : (
            <>
              {/* Explicat langa neexplicat, o data, pe bara. */}
              <div className="odb-segments" style={{ marginTop: 0 }}>
                <span
                  className="odb-seg odb-c-ok"
                  style={{ width: `${Math.min(100, Math.round((explained.explained * 100) / explained.inactive))}%` }}
                />
                <span
                  className="odb-seg odb-c-flat"
                  style={{ width: `${Math.max(0, 100 - Math.round((explained.explained * 100) / explained.inactive))}%` }}
                />
              </div>
              <div className="odb-list" style={{ flex: '1 1 auto', minHeight: 0 }}>
                {explained.by_kind.length === 0 ? (
                  <NoData>
                    Nobody wrote a line in the {explained.days} day{explained.days === 1 ? '' : 's'} of this period, so none of
                    the {durationString(explained.inactive)} of inactive time is explained.
                  </NoData>
                ) : (
                  explained.by_kind.map((kind) => (
                    <div className="odb-row" key={kind.kind}>
                      <span className="odb-row-fill" style={{ width: `${Math.round((kind.seconds * 100) / top)}%` }} />
                      <span className="odb-row-label">{kind.label}</span>
                      <span className="odb-of">
                        {kind.entries} line{kind.entries === 1 ? '' : 's'}
                      </span>
                      {/* MEDIA PE ZI-OM, nu suma peste firma si perioada: "3 zile
                          4h in sedinte" creste cu fiecare angajat, deci nu se poate
                          citi si nu se pot compara doua feluri intre ele. Numitorul
                          e cel din nota - capetele (om, zi) din rollup, nu cele care
                          au un rand scris. */}
                      <span className="odb-row-value" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {kind.avg_seconds === null ? '-' : durationString(kind.avg_seconds)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
          <p className="odb-note" style={{ marginBottom: 0 }}>
            {durationString(explained.explained)} of the {durationString(explained.inactive)} of inactive time in this period has
            a line written against it ({explained.share}%), leaving {durationString(unexplained)} nobody has explained.
            {/* Aceleasi trei cifre pe ZI-OM: sumele de deasupra cresc cu fiecare
                angajat si cu fiecare zi a perioadei, deci nu se pot citi singure si
                nu se pot compara intre doua luni. Numitorul e scris chiar aici -
                capetele (om, zi) DIN ROLLUP, peste care s-a adunat inactivul, nu
                cele care au un rand scris de om. */}
            {explained.avg_inactive !== null && (
              <>
                {' '}
                Per person-day that reported, over {explained.rollup_person_days} of them, that is{' '}
                {durationString(explained.avg_inactive)} of inactive time, of which{' '}
                {explained.avg_explained === null ? 'none' : durationString(explained.avg_explained)} is explained; the rows above
                are per person-day too.
              </>
            )}
            {explained.person_days > 0 &&
              ` ${explained.entries} line${explained.entries === 1 ? '' : 's'} over ${explained.person_days} person-day${
                explained.person_days === 1 ? '' : 's'
              }.`}{' '}
            The lines people write can overlap, so the time is the union of their intervals per person per day, never a sum - and
            for the same reason the per-kind figures can add up to more than the total. The inactive time is the one from the
            daily rollup, on the {explained.days} day{explained.days === 1 ? '' : 's'} of this period that are in it.
          </p>
        </div>
      )}
    </OdbCard>
  )
}

/**
 * DISTRIBUTIA ZILELOR-OM, in sertare de o ora.
 *
 * -----------------------------------------------------------------------------
 * DE CE O DISTRIBUTIE, SI NU O MEDIE
 * -----------------------------------------------------------------------------
 *
 * Acelasi "5h20 activ pe zi" iese dintr-o echipa in care toti lucreaza la fel si
 * dintr-una cu doua populatii, una la trei ore si una la opt. Media le confunda;
 * distributia le deosebeste dintr-o privire, si mediana spune unde e mijlocul
 * adevarat al ei.
 *
 * -----------------------------------------------------------------------------
 * CE NU DESENEAZA, SI DE CE
 * -----------------------------------------------------------------------------
 *
 * Numai zilele din rollup intra. Listele de pe ecran au voie sa cada pe sumarul
 * vechi; o distributie nu are: sumele de acolo ar muta zilele in alte sertare si
 * ar schimba chiar FORMA pentru care exista cardul, fara ca nimic sa se vada.
 * Cand n-a ramas nicio zi, cardul o SPUNE si nu deseneaza nimic.
 *
 * Sub `min_person_days` zile-om se scrie numarul, nu forma: o histograma din opt
 * puncte arata ca o masuratoare si nu e una.
 *
 * Desenul e `.odb-row` + `.odb-row-fill`, care sunt deja pe ecran. Niciun desen
 * nou.
 */
function DayDistribution({ dist }: { dist: BoardDistribution }) {
  const top = dist.buckets.length > 0 ? Math.max(...dist.buckets.map((b) => b.person_days)) : 0
  const enough = dist.person_days >= dist.min_person_days

  return (
    <OdbCard
      module="productivitee"
      icon="fa-chart-bar"
      title="Active time per person-day"
      value={dist.person_days}
      w={2}
      h={2}
      id="productivitee_board_distribution"
    >
      {(height) => (
        <div style={{ height, display: 'flex', flexDirection: 'column' }}>
          {dist.person_days === 0 ? (
            <NoData>
              {dist.legacy_person_days > 0
                ? `None of the ${dist.legacy_person_days} person-days in this period is in the daily rollup yet, and a distribution is not allowed to fall back on the old summary table: its sums would move days into other buckets and change the shape without anything showing it.`
                : 'Nobody reported a working day in this period.'}
            </NoData>
          ) : !enough ? (
            <NoData>
              Only {dist.person_days} person-day{dist.person_days === 1 ? '' : 's'} in this period, fewer than{' '}
              {dist.min_person_days}: too few for a shape. The middle one was{' '}
              {dist.median === null ? 'not measurable' : durationString(dist.median)} of active time.
            </NoData>
          ) : (
            <div className="odb-list" style={{ flex: '1 1 auto', minHeight: 0 }}>
              {dist.buckets.map((bucket) => (
                <div className="odb-row" key={bucket.hour}>
                  <span
                    className="odb-row-fill"
                    style={{ width: `${top > 0 ? Math.round((bucket.person_days * 100) / top) : 0}%` }}
                  />
                  <span className="odb-row-label">
                    {bucket.hour >= dist.max_hours ? `${dist.max_hours}h and over` : `${bucket.hour}-${bucket.hour + 1}h`}
                  </span>
                  <span className="odb-of">{Math.round((bucket.person_days * 1000) / dist.person_days) / 10}%</span>
                  <span className="odb-row-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {bucket.person_days}
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="odb-note" style={{ marginBottom: 0 }}>
            One value per person per day - the union of their intervals that day, not a sum of durations - in one-hour buckets.
            {dist.median !== null && ` The median person-day was ${durationString(dist.median)} of active time.`} A mean over
            thousands of person-days says nothing about whether the team is uniform or is two populations.
            {dist.legacy_person_days > 0 &&
              ` ${dist.legacy_person_days} person-day${dist.legacy_person_days === 1 ? '' : 's'} of this period are still in the old summary table and are left out on purpose: their sums would change the shape.`}
          </p>
        </div>
      )}
    </OdbCard>
  )
}

/**
 * Cele DOUA etichete pe care le pune butonul, cu numarul cerut de capatul de
 * scriere. A treia valoare a enumerarii (`0`) e chiar "neetichetat", deci n-are ce
 * cauta ca buton: ar fi fost un buton care lasa randul acolo unde e.
 */
const TAGS: { tag: number; label: string }[] = [
  { tag: 1, label: 'Productive' },
  { tag: 2, label: 'Unproductive' },
]

/**
 * TOP APLICATII FARA ETICHETA, cu butonul de etichetare pe rand.
 *
 * -----------------------------------------------------------------------------
 * DE CE E CARDUL CEL MAI UTIL PE O FIRMA PROASPATA
 * -----------------------------------------------------------------------------
 *
 * Pe o firma nou intrata tot catalogul e neetichetat, si atunci procentul de
 * productivitate de sus nu spune NIMIC. Cardul asta spune ce se poate face cu
 * asta: "astea sunt cele zece programe care au mancat cel mai mult timp,
 * eticheteaza-le". Cand nu mai e nimic neetichetat, scrie ca tot e etichetat si
 * dispare ca lista - un card care arata zece randuri goale ar fi cerut o munca
 * care nu mai exista.
 *
 * -----------------------------------------------------------------------------
 * BUTONUL NU E UN CAPAT NOU
 * -----------------------------------------------------------------------------
 *
 * Apasa `POST /settings/productivitee/master/tag`, acelasi pe care il apasa
 * ecranul Master Tagging: eticheta se pune pe TOATE grupurile firmei. Capatul are
 * dreptul lui, care nu vine cu cel al tabloului, si cand omul nu-l are nu se
 * deseneaza niciun buton - scrie unde se eticheteaza.
 *
 * Randul etichetat dispare PE LOC din lista, fara sa se mai ceara nimic de la
 * server: lista e tinuta minte cateva minute (e scumpa), deci o reimprospatare ar
 * fi adus-o neschimbata si butonul ar fi parut ca nu face nimic.
 */
function UntaggedApps({ apps }: { apps: BoardUntaggedApps }) {
  /* Ce s-a etichetat de pe cardul asta, in sesiunea asta de ecran. */
  const [done, setDone] = useState<Record<number, string>>({})
  const [failed, setFailed] = useState('')
  const rows = apps.rows.filter((r) => done[r.application_id] === undefined)
  const top = rows.length > 0 ? Math.max(...rows.map((r) => r.app_time)) : 0

  const tag = async (app: UntaggedApp, value: number, label: string) => {
    try {
      await post('/settings/productivitee/master/tag', { application: app.application_id, tagging: value })
      setDone((d) => ({ ...d, [app.application_id]: label }))
      setFailed('')
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'Tagging failed.')
    }
  }

  return (
    <OdbCard
      module="productivitee"
      icon="fa-tags"
      title="Untagged applications"
      value={apps.untagged}
      w={2}
      h={2}
      id="productivitee_board_untagged"
    >
      {(height) => (
        <div style={{ height, display: 'flex', flexDirection: 'column' }}>
          <AppWindow apps={apps} />
          {apps.untagged === 0 ? (
            <NoData>
              Every one of the {apps.catalog} applications this company uses is tagged, so there is nothing to sort out here.
            </NoData>
          ) : rows.length === 0 ? (
            <NoData>
              {apps.rows.length === 0
                ? `None of the ${apps.untagged} untagged applications was used in that window, so none of them ate any time.`
                : 'Tagged, all of them. The list is rebuilt on the next read.'}
            </NoData>
          ) : (
            <div className="odb-list" style={{ flex: '1 1 auto', minHeight: 0 }}>
              {rows.map((app) => (
                <div className="odb-row" key={app.application_id}>
                  <span className="odb-row-fill" style={{ width: `${top > 0 ? Math.round((app.app_time * 100) / top) : 0}%` }} />
                  <span className="odb-row-label" title={`${app.application_name || 'no name'} (${app.application_type})`}>
                    {app.application_name || <span className="odb-unknown-why">no name</span>}
                  </span>
                  {/* MEDIA PE ZI-OM, nu suma ferestrei peste toata firma: "2 zile
                      3h" nu spunea daca merita apasat butonul. Pe trei sute de
                      oameni si sapte zile aia e un program deschis din greseala;
                      pe trei oameni e jumatate din munca lor. Numitorul e pe fata
                      cardului, deasupra listei. Randul nu capata nicio celula
                      noua - la 1280 cardul are 598px si doua butoane pe rand. */}
                  <span className="odb-row-value" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {app.avg_time === null ? '-' : durationString(app.avg_time)}
                  </span>
                  {apps.can_tag && (
                    <span style={{ display: 'flex', gap: 4, flex: '0 0 auto', marginLeft: 8 }}>
                      {TAGS.map((t) => (
                        <button
                          type="button"
                          key={t.tag}
                          className="odb-window"
                          onClick={() => void tag(app, t.tag, t.label)}
                          title={`Tag ${app.application_name || 'this application'} as ${t.label.toLowerCase()} on every group of the company`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {Object.keys(done).length > 0 && (
            <p className="odb-note" style={{ marginBottom: 0 }}>
              Tagged just now: {Object.values(done).length} application{Object.values(done).length === 1 ? '' : 's'}.
            </p>
          )}
          {failed !== '' && (
            <p className="odb-note odb-lead-warn" style={{ marginBottom: 0 }}>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" /> {failed}
            </p>
          )}
          <p className="odb-note" style={{ marginBottom: 0 }}>
            {apps.untagged} of the {apps.catalog} applications this company uses have no tag on any group. The time on each row is
            the average per person-day of the window written above, not a sum over it: a sum grew with every extra person, so the
            same figure meant a stray program on a company of three hundred and half the work on a company of three. This and
            Where the day goes are the two figures on the screen read from the history table, so it is kept for up to{' '}
            {Math.round(apps.cached_seconds / 60)} minutes instead of being asked again on every load.
            {apps.can_tag
              ? ' Tagging puts the tag on every group of the company, the same as Settings > Productivitee > Master Tagging.'
              : ' Tagging is in Settings > Productivitee > Master Tagging, which has its own right.'}
          </p>
        </div>
      )}
    </OdbCard>
  )
}

/**
 * CAT DE INCHISA e celula, dupa cate zile la rand tace masina.
 *
 * Rampa `--odb-heat-0..5` e o scara de ROSU si se foloseste AICI ASA CUM E:
 * lipsa de date chiar e rau, si cu cat tine mai mult, cu atat e mai rau. Treapta
 * 0 e pala si nu inseamna "bine", inseamna "nimic de aratat" - ziua raportata si
 * ziua despre care nu se poate spune nimic o poarta amandoua.
 *
 * NU se foloseste pentru productivitate: acolo culoarea ar fi spus "rau" despre
 * un om sau un departament, ceea ce o masuratoare de timp n-are cum sa sustina.
 */
function silenceStep(run: number): number {
  if (run <= 0) {
    return 0
  }
  if (run <= 3) {
    return run
  }

  return run <= 6 ? 4 : 5
}

/** Un nume de zi scurt pentru sfaturile de deasupra celulelor: `2026-09-10`. */
function dayTitle(day: string, state: 'reported' | 'silent' | 'nobody', run: number): string {
  if (state === 'reported') {
    return `${day} - reported`
  }
  if (state === 'nobody') {
    return `${day} - nobody reported at all, so this is a collection outage, not a silent machine`
  }

  return `${day} - silent, ${run} ${run === 1 ? 'day' : 'days'} in a row`
}

/**
 * HARTA "CINE TACE, SI DE CAND".
 *
 * Banda de sus spune "9 / 12 masini". Harta spune CARE si DE CAND, din PREZENTA
 * randului pe (masina, zi) - nicio suma, nicio impartire de secunde. Desenul e
 * cel scris in foaie pentru harti de caldura (`.odb-heat*`), nu unul nou.
 *
 * CE SE VEDE CAND NU SUNT DATE. O firma proaspata, sau o perioada dinaintea
 * rollup-ului, are grila TOATA PALA si un rand care spune ca nicio masina n-a
 * raportat inca - raspunsul corect, spre deosebire de un "0%" care ar suna ca o
 * masuratoare. Iar o zi in care n-a raportat NIMENI ramane o coloana pala, nu una
 * rosie: aia e o cadere de colectare, si nu se pune in sarcina masinilor.
 */
function SilenceHeat({ silence }: { silence: BoardSilence }) {
  const reporting = new Map(silence.by_day.map((d) => [d.day, d.devices]))
  const nothingAtAll = silence.by_day.every((d) => d.devices === 0)
  const stillSilent = silence.devices.filter((d) => d.reported === 0).slice(0, 6)

  return (
    <OdbCard
      module="productivitee"
      icon="fa-desktop"
      title="Who is silent, and since when"
      value={`${silence.silent} / ${silence.expected}`}
      w={2}
      h={2}
      id="productivitee_board_silence"
    >
      {(height) => (
        <div style={{ height, display: 'flex', flexDirection: 'column' }}>
          {silence.expected === 0 ? (
            <NoData>
              No machine here passes the seven gates of the machine list (not in stock, agent installed, compliance above zero, not
              removed), so there is nobody to expect a report from.
            </NoData>
          ) : (
            <>
              {nothingAtAll && (
                <p className="odb-note" style={{ marginTop: 0 }}>
                  No machine reported a day in this window. The cells come from the daily rollup, so a window from before the
                  rollup was written is empty even if the machines were working.
                </p>
              )}
              <div className="odb-list" style={{ flex: '1 1 auto', minHeight: 0 }}>
                <div className="odb-heat" style={{ marginTop: 0 }}>
                  {silence.devices.map((device) => {
                    const said = new Set(device.days)
                    let run = 0

                    return (
                      <div
                        className="odb-heat-row"
                        key={device.device_id}
                        title={`${device.name || 'no name'} - reported ${device.reported} of ${silence.days.length} days, ${
                          device.last_day === null ? 'never reported at all' : `last on ${device.last_day}`
                        }`}
                      >
                        <span className="odb-heat-name">{device.name || 'no name'}</span>
                        <span className="odb-heat-cells">
                          {silence.days.map((day) => {
                            /* O zi in care n-a raportat nimeni nu spune nimic despre
                               masina asta: nu se coloreaza, si nu lungeste tacerea. */
                            const state = (reporting.get(day) ?? 0) === 0 ? 'nobody' : said.has(day) ? 'reported' : 'silent'
                            if (state === 'reported') {
                              run = 0
                            } else if (state === 'silent') {
                              run += 1
                            }
                            const step = state === 'silent' ? silenceStep(run) : 0

                            return <span className={`odb-heat-cell odb-heat-${step}`} key={day} title={dayTitle(day, state, run)} />
                          })}
                        </span>
                        <span className="odb-heat-total">{device.reported}</span>
                      </div>
                    )
                  })}
                </div>
                <div className="odb-heat-axis">
                  <span>{silence.from}</span>
                  <span>{silence.to}</span>
                </div>
                <div className="odb-heat-key">
                  <span className="odb-heat-key-said">reported or unknown</span>
                  <span className="odb-heat-cell odb-heat-0" />
                  <span className="odb-heat-cell odb-heat-1" />
                  <span className="odb-heat-cell odb-heat-3" />
                  <span className="odb-heat-cell odb-heat-5" />
                  <span className="odb-heat-key-said">silent, a day to a week and more</span>
                </div>
              </div>
            </>
          )}
          <p className="odb-note" style={{ marginBottom: 0 }}>
            One cell per machine per day, from whether the machine wrote a row that day - no times are added up here, so this is
            the one figure on the screen that cannot be off.
            {stillSilent.length > 0 && (
              <>
                {' '}
                Silent the whole window:{' '}
                {stillSilent
                  .map((d) => `${d.name || 'no name'} (${d.last_day === null ? 'never' : `last ${d.last_day}`})`)
                  .join(', ')}
                {silence.silent > stillSilent.length ? `, and ${silence.silent - stillSilent.length} more` : ''}.
              </>
            )}
            {/* Randul asta se scrie numai cand harta CHIAR a fost taiata la limita,
                nu de fiecare data cand o masina n-are rand de desenat. */}
            {silence.devices.length >= silence.limit &&
              silence.expected > silence.devices.length &&
              ` The ${silence.devices.length} quietest machines are drawn, of ${silence.expected} expected.`}
            {silence.trimmed && ` The window is trimmed to the last ${silence.days.length} days of the period.`}
          </p>
        </div>
      )}
    </OdbCard>
  )
}

/**
 * De cate ori imprastierea LUI INSUSI se cere ca un panou sa se aprinda.
 * Acelasi numar ca `Productivitee::JUMP_SPREADS` - scris aici o data, numai ca
 * sa poata fi SPUS pe ecran; socoteala se face pe server.
 */
const JUMP_SPREADS = 3

/** Sub atatea trepte cu raport, semnul de "a sarit" nu se pune. Vezi `Productivitee::JUMP_MIN_BUCKETS`. */
const JUMP_MIN_BUCKETS = 6

/**
 * LINIA unui panou, pe scara comuna 0-100%.
 *
 * Scara e comuna si asta e tot rostul panourilor puse unul langa altul: cu scara
 * fiecaruia, un departament la 40% si unul la 90% ar avea exact acelasi desen, si
 * comparatia - singurul motiv pentru care stau alaturi - ar disparea. Procentul
 * are capetele lui firesti, deci scara e 0-100, nu maximul randului.
 *
 * Treptele fara raport (`share` null) NU sunt desenate ca zero: ar fi aratat ca o
 * prabusire acolo unde de fapt n-a lucrat nimeni. Ele se sar, iar cele care raman
 * isi tin locul pe orizontala, deci un gol se vede ca o bucata mai lunga de linie.
 *
 * Acelasi desen ca `Draw::multiples()` din catalogul tablourilor - aceeasi foaie,
 * acelasi `viewBox`, aceleasi doua forme (umbra si linia).
 */
function PanelLine({ points }: { points: BoardPanelPoint[] }) {
  const step = points.length > 1 ? 100 / (points.length - 1) : 0
  const drawn = points
    .map((p, i) => (p.share === null ? null : `${Math.round(i * step * 100) / 100},${Math.round((26 - (p.share / 100) * 24) * 100) / 100}`))
    .filter((p): p is string => p !== null)

  if (drawn.length < 2) {
    return <span className="odb-multi-flat" />
  }
  const line = drawn.join(' ')

  return (
    <svg className="odb-multi-line" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={`0,28 ${line} 100,28`} />
      <polyline points={line} />
    </svg>
  )
}

/**
 * Tonul panoului: aprins numai cand a SARIT, si atunci in jos e grav, in sus e
 * bine. Nicio culoare pentru cine sta pe locul lui - un departament al treilea
 * intr-o lista nu e o stare.
 *
 * NU se foloseste rampa `--odb-heat-*`: aia e o scara de ROSU, pentru cat de rau
 * e ceva, si aici nu se masoara rau-bine, se masoara schimbare.
 */
function panelTone(panel: BoardPanel): 'crit' | 'ok' | 'flat' {
  if (!panel.jumped || panel.jump === null) {
    return 'flat'
  }

  return panel.jump < 0 ? 'crit' : 'ok'
}

/** Randul de sub linie: ce spune panoul, in cuvinte, sau de ce nu spune nimic. */
function panelSaid(panel: BoardPanel, grain: string): string {
  if (!panel.comparable) {
    return `Too few people to compare - ${panel.people} with data in this period, and the median needs more than two.`
  }
  if (panel.reported_buckets === 0) {
    return `Nobody in this department reported a ${grain} in this period.`
  }
  if (panel.median === null) {
    return `One ${grain} with a report, so there is no own median to compare it against yet.`
  }
  if (panel.jumped && panel.jump !== null) {
    return `${panel.jump > 0 ? 'Up' : 'Down'} ${Math.abs(panel.jump)} points on its own median of ${panel.median}%.`
  }
  if (!panel.scored) {
    return `${panel.reported_buckets} ${grain}s with a report, fewer than ${JUMP_MIN_BUCKETS}, so no jump is marked. Own median ${panel.median}%.`
  }

  return `On its own median of ${panel.median}%.`
}

/**
 * CARDUL CU PANOURI MICI, cate unul pe departament.
 *
 * A luat locul clasamentului de departamente, nu s-a asezat sub el: cele doua
 * raspundeau la aceeasi intrebare, iar clasamentul raspundea prost - dominat de
 * masini uitate aprinse, nu se schimba de la o zi la alta si nu se mai uita
 * nimeni la el. Clasamentul de SEDII a ramas: sediul e alta taietura, si cardul
 * asta nu-l acopera.
 *
 * Departamentul unui om e imprumutat de la masinile pe care a lucrat - oamenii
 * n-au departament al lor in partea asta a modulului - si scrie pe card.
 */
function DepartmentPanels({ panels, grain }: { panels: BoardPanel[]; grain: string }) {
  return (
    <OdbCard module="productivitee" icon="fa-sitemap" title="Departments, each against itself" value={panels.length} w={2} h={2} id="productivitee_board_departments">
      {(height) => (
        <div style={{ height, display: 'flex', flexDirection: 'column' }}>
          {panels.length === 0 ? (
            <NoData>Nobody reported a working day in this period.</NoData>
          ) : (
            <div className="odb-list" style={{ flex: '1 1 auto', minHeight: 0 }}>
              <div className="odb-multi-grid" style={{ marginTop: 0 }}>
                {panels.map((panel) => {
                  const said = panelSaid(panel, grain)

                  return (
                    <span className={`odb-multi odb-t-${panelTone(panel)}`} key={`${panel.id}-${panel.name}`} title={`${panel.name} - ${said}`}>
                      <span className="odb-multi-head">
                        <span className="odb-multi-name">{panel.name}</span>
                        <span className="odb-multi-value">{panel.latest === null ? '-' : `${panel.latest}%`}</span>
                      </span>
                      {panel.comparable ? <PanelLine points={panel.points} /> : <span className="odb-multi-flat" />}
                      <span className="odb-multi-key" style={{ marginTop: 4, display: 'block' }}>
                        {panel.people} {panel.people === 1 ? 'person' : 'people'} &middot; {said}
                      </span>
                    </span>
                  )
                })}
              </div>
            </div>
          )}
          <p className="odb-note" style={{ marginBottom: 0 }}>
            Same scale on every panel: 0-100% active time, by {grain}. A panel lights up when its latest {grain} is at least{' '}
            {JUMP_SPREADS} times its own spread away from its own median - never against a threshold shared with the other
            departments, which would just repeat a ranking in colours. Departments are borrowed from the machines people worked on
            (it_echipamente.GroupID), each person put in the one they spent the most days on.
          </p>
        </div>
      )}
    </OdbCard>
  )
}

export function ProductiviteeManagementPage() {
  const [period, setPeriod] = useState<Period>(lastThirtyDays)
  const [office, setOffice] = useState(-1)
  const [metric, setMetric] = useState<Metric>('share')
  const shell = useQuery(shellQuery('productivitee'))
  /*
   * Cand antetul are ALES un sediu, ecranul ramane in el si selectorul nu mai
   * are ce alege - exact cum se ingusteaza si celelalte trei liste ale
   * modulului. Capatul face la fel, si el e cel care hotaraste: un sediu scris
   * de mana in adresa nu scoate pe nimeni din sediul lui.
   */
  const locked = (shell.data?.officeId ?? -1) > -1
  const q = useQuery(managementQuery(period.from, period.to, locked ? -1 : office))
  const d = q.data
  const honesty = d?.honesty

  const peopleShare = honesty && honesty.people_expected > 0 ? Math.round((honesty.people_reported / honesty.people_expected) * 100) : 0
  const deviceShare = honesty && honesty.devices_expected > 0 ? Math.round((honesty.devices_reported / honesty.devices_expected) * 100) : 0

  /* Pastila apasata e cea a carei perioada e chiar cea de acum - aceeasi
     socoteala ca in `PeriodWithPresets`, din care vin si numele. */
  const preset = PERIOD_PRESETS.find((name) => {
    const r = rangeOf(name)
    return r.from.slice(0, 10) === period.from && r.to.slice(0, 10) === period.to
  }) ?? ''

  const bar = (
    <div className="odb-bar-top">
      <h1 className="odb-name">Management Board</h1>
      {/* Ferestrele de timp, in banda de pastile a tablourilor. Butoane, nu
          legaturi: alegerea se face pe loc si adresa nu se schimba - vezi
          `button.odb-window` din `od_dashboard.css`. */}
      <nav className="odb-windows" aria-label="Period">
        {PERIOD_PRESETS.map((name) => (
          <button
            type="button"
            key={name}
            className={`odb-window${preset === name ? ' is-on' : ''}`}
            onClick={() => {
              const r = rangeOf(name)
              setPeriod({ from: r.from.slice(0, 10), to: r.to.slice(0, 10) })
            }}
          >
            {name}
          </button>
        ))}
      </nav>
      {/* Ce masura deseneaza graficul. Numele vin din `METRIC_LABEL`, acelasi
          din care isi ia numele si fila Evolution a unui om. */}
      <nav className="odb-views" aria-label="Chart metric">
        {METRICS.map((m) => (
          <button type="button" key={m} className={`odb-view${metric === m ? ' is-on' : ''}`} onClick={() => setMetric(m)}>
            {METRIC_LABEL[m]}
          </button>
        ))}
      </nav>
      {/*
        `form-inline`, nu numai `.odb-bar-right`: `form-control` din Bootstrap e
        `display: block; width: 100%`, iar intr-un rand flex asta ar intinde
        casutele cat tine bara - vezi Endpoint Performance, unde s-a masurat.
      */}
      <div className="odb-bar-right form-inline">
        <select
          className="form-control input-sm"
          aria-label="Office"
          style={{ width: 'auto' }}
          value={locked ? String(shell.data?.officeId ?? '') : String(office)}
          disabled={locked}
          title={locked ? 'The header is on one office, so this screen stays in it.' : undefined}
          onChange={(e) => setOffice(Number(e.target.value))}
        >
          {locked ? (
            <option value={String(shell.data?.officeId ?? '')}>{shell.data?.offices.find((o) => o.id === shell.data?.officeId)?.name ?? 'Selected office'}</option>
          ) : (
            <>
              <option value="-1">All offices</option>
              {(shell.data?.offices ?? []).map((o) => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
            </>
          )}
        </select>
        {/* Perioada scrisa de mana - "Custom range" al selectorului de dinainte.
            Aceleasi doua casute, acelasi `DatePicker`. */}
        <PeriodFilter period={period} onChange={setPeriod} />
      </div>
    </div>
  )

  if (q.error) {
    return (
      <BoardLook>
        {bar}
        <div className="odb-empty">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Something went wrong: {q.error.message}
        </div>
      </BoardLook>
    )
  }

  if (!d || !honesty) {
    return (
      <BoardLook>
        {bar}
        <div className="odb-empty">
          Reading {period.from} - {period.to}…
        </div>
      </BoardLook>
    )
  }

  return (
    <BoardLook>
      {bar}

      {/*
        CIFRELE DE SUS, in banda tablourilor. Primele doua sunt raspunsul
        ecranului, cu perioada dinainte scrisa deasupra titlului; celelalte trei
        sunt cele de onestitate, si se APRIND cand trec de prag - cu tonul
        cardurilor, fiindca in zona asta culoarea inseamna stare.
      */}
      <BoardStrip>
        <BoardStat
          module="productivitee"
          icon="fa-chart-line"
          value={d.totals.share}
          unit="%"
          kicker={`was ${d.previous_totals.share}%`}
          title={METRIC_LABEL.share}
        />
        {/*
          MEDIA PE ZI-OM, nu suma perioadei - Roxana, 25 septembrie 2026: "in
          dashboarduri tot average nu mai face sume ca nu e relevant". "1247h"
          pe o luna nu spunea nimic despre firma: crestea cu fiecare angajat si
          cu fiecare zi de perioada, deci se schimba si cand nimeni nu lucra
          altfel. Numitorul - cate zile-om au raportat - sta in `kicker`, fiindca
          o medie fara numitor minte.
        */}
        <BoardStat
          module="productivitee"
          icon="fa-hourglass-half"
          value={d.totals.avg_working_time === null ? '-' : durationString(d.totals.avg_working_time)}
          kicker={`over ${d.totals.days} person-days, was ${d.previous_totals.avg_working_time === null ? '-' : durationString(d.previous_totals.avg_working_time)}`}
          title={`${METRIC_LABEL.working_time} / person-day`}
        />
        <BoardStat
          module="productivitee"
          tone={peopleShare < POOR_COVERAGE ? 'crit' : 'flat'}
          icon="fa-user-check"
          value={`${honesty.people_reported} / ${honesty.people_expected}`}
          kicker={`${peopleShare}% of the people`}
          title="People reporting"
        />
        <BoardStat
          module="productivitee"
          tone={deviceShare < POOR_COVERAGE ? 'crit' : 'flat'}
          icon="fa-desktop"
          value={`${honesty.devices_reported} / ${honesty.devices_expected}`}
          kicker={`${deviceShare}% of the machines`}
          title="Machines reporting"
        />
      </BoardStrip>

      {/* Ce inseamna procentul din banda, o data pe ecran. */}
      <p className="odb-lead" style={{ marginBottom: 'var(--odb-gap)' }}>{SHARE_HINT}</p>

      {/*
        CE TREBUIE CITIT INAINTE DE CIFRELE DE DEDESUBT.
        `.odb-lead` e randul pe care tablourile il folosesc pentru asta, si
        `.odb-lead-warn` il aprinde. Sta sub banda si deasupra grilei: "the
        figures above" chiar arata spre cifrele de deasupra lui, iar
        clasamentele de dedesubt nu mai au nimic lipit de capul lor.
      */}
      {(peopleShare < POOR_COVERAGE || deviceShare < POOR_COVERAGE) && (
        <p className="odb-lead" style={{ marginBottom: 'var(--odb-gap)' }}>
          {peopleShare < POOR_COVERAGE && (
            <span className="odb-lead-warn" style={{ display: 'block' }}>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" /> only {peopleShare}% of the people wrote a
              day - the figures above are theirs, not the company&apos;s
            </span>
          )}
          {deviceShare < POOR_COVERAGE && (
            <span className="odb-lead-warn" style={{ display: 'block' }}>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" /> only {deviceShare}% of the machines wrote a
              summary in this period
            </span>
          )}
        </p>
      )}

      {/*
        CAT DIN ZI E UN PUNCT ORB - a treia cifra de onestitate, intoarsa.
        ---------------------------------------------------------------------
        A plecat pe 24 septembrie 2026 cu tot cu cardul "Measured time", si pe
        drept: se citea din `it_productivitee_history.tag`, iar de la aceeasi data
        randul fara eticheta se scrie acolo cu `tag = 1`, deci felia ar fi fost de
        acum incolo mereu ZERO fix pe firmele care n-au etichetat nimic.

        Se intoarce socotita din partea care CHIAR stie: tabelul de ETICHETARE,
        peste toate aplicatiile ferestrei. Asta nu e o infrumusetare - e capcana
        ecranului: pe instanta asta programul neetichetat se socoteste PRODUCTIV,
        deci procentul din banda de sus e in parte o presupunere, si pana acum
        nimic pe ecran nu spunea cat de mare e partea aia.

        E un rand de `.odb-lead`, nu un card: e ceva ce trebuie citit INAINTE de
        cifrele de dedesubt, si nu cere niciun loc in grila. Nu se APRINDE la
        niciun prag - n-am niciun numar pe care sa-l pot apara pentru "prea mult
        neetichetat", si un prag inventat s-ar fi citit ca o masuratoare.
      */}
      {d.top_apps && d.top_apps.untagged_share > 0 && (
        <p className="odb-lead" style={{ marginBottom: 'var(--odb-gap)' }}>
          <i className="fas fa-info-circle" aria-hidden="true" /> {d.top_apps.untagged_share}% of the{' '}
          {d.top_apps.trimmed ? `last ${d.top_apps.days} days` : 'period'} went into {d.top_apps.untagged_apps} application
          {d.top_apps.untagged_apps === 1 ? '' : 's'} with no tag on any group yet. Time like that is counted as productive, so for
          that part of the day the figures above are an assumption, not a measurement. The Untagged applications card ranks them by
          how much they took.
        </p>
      )}

      {/*
        DIN CE TABEL VIN ZILELE. Rollup-ul pe (om, zi) se scrie doar de la
        livrarea lui incoace, iar zilele dinainte se umplu una cate una cu
        `od:productivitee:rollup --day=...`. Pana atunci o perioada lunga vine
        din amandoua tabelele, si cele doua nu socotesc la fel aceeasi zi:
        rollup-ul REUNESTE intervalele, sumarul vechi ADUNA duratele. Randul se
        deseneaza numai cand chiar s-au amestecat.
      */}
      {(d.sources?.legacy_days ?? 0) > 0 && (
        <p className="odb-lead" style={{ marginBottom: 'var(--odb-gap)' }}>
          <i className="fas fa-info-circle" aria-hidden="true" /> {d.sources.legacy_days} of the {d.sources.reported_days} days
          with data ({d.sources.legacy_from} - {d.sources.legacy_to}) still come from the old summary table; the rest come from
          the daily rollup. The old table sums durations, the rollup unions intervals, so the two do not give the same figure
          for the same day.
        </p>
      )}

      {d.explained && !d.explained.enabled && (
        <p className="odb-lead" style={{ marginBottom: 'var(--odb-gap)' }}>
          <i className="fas fa-info-circle" aria-hidden="true" /> The evening report is off for this company, so nobody has been
          asked about the gaps in their day and there is nothing to show about them. It is turned on in Settings &gt; Productivitee
          &gt; General settings.
        </p>
      )}

      <BoardGrid>
        <OdbCard
          module="productivitee"
          icon="fa-chart-area"
          title={`${METRIC_LABEL[metric]} by ${d.grain}`}
          w={2}
          h={2}
          id="managementTrend"
        >
          {(height) =>
            d.buckets.length === 0 ? (
              <NoData>No day of this period has a summary.</NoData>
            ) : (
              <>
                <TrendChart buckets={d.buckets} previous={d.previous.buckets} metric={metric} height={height - 22} />
                <TrendLegend now={`${d.from} - ${d.to}`} before={`${d.previous.from} - ${d.previous.to}`} />
              </>
            )
          }
        </OdbCard>

        {/*
          UNDE SE DUCE ZIUA. Aici statea "Measured time" - trei durate adunate
          peste toata firma, fara numitor, si citite din coloana `tag` a
          istoricului, care de pe 24 septembrie 2026 nu mai poate spune ce e
          neetichetat. Cardul nou sta in acelasi loc fiindca randul de sus al
          grilei e al RASPUNSULUI ecranului: cat, in stanga, si in ce, aici.
        */}
        {d.top_apps && <TopApps apps={d.top_apps} />}

        <Ranking
          id="productivitee_board_offices"
          title="Offices"
          icon="fa-building"
          rows={d.offices}
          empty="Nobody reported a working day in this period."
          note="The two times are averages per person-day, not sums over the period: a sum grows with every extra person and every extra day, so it ranked offices by size instead of by how they work. Only the person-days that reported are counted in the denominator - a holiday is left out, not divided into. The office is the one written on the person (it_logged_users_list.office_id), not the one written on the machine. One row per person per day, so somebody with two machines is counted once."
        />

        <DepartmentPanels panels={d.departments} grain={d.grain} />

        {d.silence && <SilenceHeat silence={d.silence} />}

        {d.untagged_apps && <UntaggedApps apps={d.untagged_apps} />}

        {d.distribution && <DayDistribution dist={d.distribution} />}

        {/*
          CARDUL "EXPLAINED GAPS" LIPSESTE cand raportul de seara e stins - si
          asta e implicitul fiecarei firme. Un zero desenat convingator ar fi
          aratat ca nimeni n-are nimic de spus, cand de fapt nimeni n-a fost
          intrebat. In locul lui, un rand care spune unde se aprinde.
        */}
        {d.explained?.enabled && <ExplainedGaps explained={d.explained} />}
      </BoardGrid>
    </BoardLook>
  )
}
